import React, { useState } from 'react';
import {
  Box,
  Popper,
  styled,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Tooltip from '@src/components/Tooltip';
import NoData from '@src/components/NoData';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createFilterOptions } from '@mui/material/Autocomplete';
import theme from '@src/styles/theme';
import { FormGroup, InfoText, StyledAutocomplete } from './index.styles';

const CustomOptions = ({
  headerOption,
  defaultValue,
  onChange,
  infoOption,
  detailInfoOption,
  handleClickShowPopup,
  options,
  inputType,
  dataId,
  dataKey,
  showInfoOptionBottom,
  showInfoOptionTop,
  note,
  customStyles,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const [inputValue, setInputValue] = useState('');
  const [valueSelected, setValueSelected] = useState(defaultValue);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formatThousandSeparator = (value) => {
    if (inputType === 'number') {
      const newValue = value.replace(/\D/g, '');
      return newValue ? Number(newValue).toLocaleString(language) : '';
    }
    return value;
  };
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => formatThousandSeparator(option?.label),
  });

  const onInputChange = (_, newInputValue) => {
    setInputValue(newInputValue);
  };
  const compareOptionWithDefaultValue = (option, defaultOption) =>
    option.value === defaultOption.value;
  const onSelectChange = (event, newValue) => {
    if (newValue) setValueSelected(newValue);
    if (newValue && onChange) onChange(newValue);
  };

  const CustomNoOptions = () => <NoData dropTable noDataText={t('noData')} />;
  const renderInput = (params) => (
    <TextField
      {...params}
      inputProps={{
        ...params.inputProps,
        inputMode: 'numeric',
        pattern: '[0-9]*',
      }}
    />
  );
  const renderTooltip = () => {
    if (!detailInfoOption) return null;
    if (isMobile) {
      return (
        <InfoRoundedIcon onClick={handleClickShowPopup} className="info-icon" />
      );
    }
    return (
      <Tooltip
        arrow
        className="tootlip"
        title={detailInfoOption}
        placement="right"
        maxWidth="100%"
      >
        <InfoRoundedIcon className="info-icon" />
      </Tooltip>
    );
  };

  const CustomPopper = styled(Popper)(() => ({
    zIndex: '99999 !important',
  }));

  return (
    <FormGroup style={{ flex: 1 }}>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        className="container-header-option"
      >
        <Box>{headerOption}</Box>
        {showInfoOptionTop && (
          <InfoText className="container-info-header-option">
            <Typography className="info-text">{infoOption}</Typography>
            {renderTooltip()}
          </InfoText>
        )}
      </Box>
      <Box>
        <StyledAutocomplete
          PopperComponent={CustomPopper}
          options={options}
          inputValue={formatThousandSeparator(inputValue)}
          onInputChange={onInputChange}
          renderInput={renderInput}
          defaultValue={defaultValue}
          value={valueSelected}
          disableClearable={!isMobile}
          isOptionEqualToValue={compareOptionWithDefaultValue}
          noOptionsText={<CustomNoOptions />}
          onChange={onSelectChange}
          popupIcon={<ExpandMoreIcon style={{ color: '#1E164EA3' }} />}
          getOptionLabel={(option) =>
            formatThousandSeparator(option?.label) || option || ''
          }
          renderOption={(p, option) => (
            <div
              data-id={dataId}
              data-char-count={
                dataKey === 'credits'
                  ? props?.value?.value || valueSelected?.value
                  : undefined
              }
              data-device-count={
                dataKey === 'devices'
                  ? props?.value?.value || valueSelected?.value
                  : undefined
              }
              data-voice-clone-count={
                dataKey === 'voiceClonings'
                  ? props?.value?.value || valueSelected?.value
                  : undefined
              }
              {...p}
            >
              {formatThousandSeparator(option?.label) || option || ''}
            </div>
          )}
          filterOptions={filterOptions}
          ListboxProps={{
            sx: {
              '& .MuiAutocomplete-option': {
                fontSize: '14px',
                color: '#1E164E',
              },
            },
          }}
          customStyles={customStyles}
          {...props}
        />
        {showInfoOptionBottom && (
          <InfoText className="container-info-option">
            <Typography className="info-text">{infoOption}</Typography>
            {renderTooltip()}
          </InfoText>
        )}
        {note && (
          <InfoText>
            <Typography className="info-text">{note}</Typography>
          </InfoText>
        )}
      </Box>
    </FormGroup>
  );
};

export default CustomOptions;
